import React, { useState } from "react";
import { KeyRound, Mail, Save } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const AccountManager = ({ authedAxios, user, onUpdated }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Inserisci la password attuale");
      return;
    }
    if (!newEmail && !newPassword) {
      toast.error("Modifica almeno email o password");
      return;
    }
    if (newPassword) {
      if (newPassword.length < 6) {
        toast.error("La nuova password deve avere almeno 6 caratteri");
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error("Le password non coincidono");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (newEmail) payload.new_email = newEmail.trim();
      if (newPassword) payload.new_password = newPassword;
      const res = await authedAxios().put("/auth/me", payload);
      toast.success("Account aggiornato");
      setCurrentPassword("");
      setNewEmail("");
      setNewPassword("");
      setConfirmPassword("");
      if (onUpdated) onUpdated(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore aggiornamento";
      toast.error(typeof msg === "string" ? msg : "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="admin-account-section" className="mb-14">
      <p className="overline text-[color:var(--wine)] flex items-center mb-6">
        <span className="section-line"></span>
        Account
      </p>
      <h2 className="font-serif-display text-3xl mb-2">Le tue credenziali.</h2>
      <p className="text-sm text-[color:var(--ink-3)] mb-8">
        Email corrente: <span className="font-medium text-[color:var(--ink-2)]">{user?.email}</span>
      </p>

      <form
        onSubmit={submit}
        data-testid="account-form"
        className="bg-[color:var(--ivory-2)] border border-[color:var(--line)] p-6 lg:p-8 max-w-2xl"
      >
        <div className="space-y-6">
          <div>
            <Label htmlFor="acc-current" className="overline text-[color:var(--ink-3)]">
              Password attuale (richiesta)
            </Label>
            <Input
              id="acc-current"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid="account-current-password"
              required
              className="mt-2 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-lg"
            />
          </div>

          <div className="pt-4 border-t border-[color:var(--line)]">
            <p className="overline text-[color:var(--ink-3)] flex items-center gap-2 mb-4">
              <Mail size={12} strokeWidth={1.6} /> Cambia email
            </p>
            <Label htmlFor="acc-email" className="overline text-[color:var(--ink-3)]">
              Nuova email (opzionale)
            </Label>
            <Input
              id="acc-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder={user?.email}
              data-testid="account-new-email"
              className="mt-2 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-lg"
            />
          </div>

          <div className="pt-4 border-t border-[color:var(--line)]">
            <p className="overline text-[color:var(--ink-3)] flex items-center gap-2 mb-4">
              <KeyRound size={12} strokeWidth={1.6} /> Cambia password
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="acc-new" className="overline text-[color:var(--ink-3)]">
                  Nuova password
                </Label>
                <Input
                  id="acc-new"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="account-new-password"
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-lg"
                />
                <p className="mt-1 text-[10px] text-[color:var(--ink-3)]">Min. 6 caratteri</p>
              </div>
              <div>
                <Label htmlFor="acc-confirm" className="overline text-[color:var(--ink-3)]">
                  Conferma nuova password
                </Label>
                <Input
                  id="acc-confirm"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="account-confirm-password"
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--line)] bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-lg"
                />
              </div>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          data-testid="account-save"
          className="mt-8 btn-primary disabled:opacity-50"
        >
          <Save size={14} strokeWidth={1.6} />
          {saving ? "Salvataggio..." : "Salva modifiche"}
        </button>
      </form>
      <p className="mt-4 text-xs text-[color:var(--ink-3)] italic max-w-2xl">
        Suggerimento: dopo aver cambiato la password, il token viene rinnovato automaticamente — non serve rifare login.
      </p>
    </section>
  );
};

export default AccountManager;
